import { ChangeEvent, SyntheticEvent, useState } from "react";
import { camelCase, startCase } from "lodash";
import { QuestionValueProps } from "@/pages/quiz/context/types";

// Interface for validation field properties
interface ValidationField {
  regex?: RegExp;
  regexFailMessage?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  customValidator?: (value: string) => string | void;
}

interface IUseManageInputFieldsProps {
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void;
}
// Type for validation fields
type ValidationFields<T extends string = string> = Record<T, ValidationField>;

// Custom hook to manage input fields
export const useManageInputFields = <
  AnsweredType extends Record<string, string>,
  ValidationKeys extends string = string
>(
  {
    setQuestionValue,
    onBlur,
    questionValue,
  }: QuestionValueProps<AnsweredType> & IUseManageInputFieldsProps,
  validationFields: Partial<ValidationFields<ValidationKeys>>
) => {
  const [error, setError] = useState<Record<string, string | boolean>>({});

  const setFieldError = (field: string, errorMessage: string | boolean) => {
    setError((errors) => ({ ...errors, [field]: errorMessage }));
  };

  const transformKey = (key: string) => startCase(camelCase(key));

  const validateField = (
    name: ValidationKeys,
    value: string
  ): string | false => {
    const validationField = validationFields[name];
    if (!validationField) {
      return false;
    }

    const trimmedValue = (value || "").trim();

    if (validationField.customValidator) {
      const msg = validationField.customValidator(value);
      if (msg) {
        return msg;
      }
    }

    if (validationField.required && !trimmedValue) {
      return "This field is required.";
    }

    if (!validationField.required && !trimmedValue) {
      return false;
    }

    if (
      validationField.minLength &&
      trimmedValue.length < validationField.minLength
    ) {
      return `The ${transformKey(name)} must be at least ${
        validationField.minLength
        } characters long.`;
    }

    if (
      validationField.maxLength &&
      trimmedValue.length > validationField.maxLength
    ) {
      return `The ${transformKey(name)} must be at most ${
        validationField.maxLength
      } characters long.`;
    }

    if (validationField.regex && !validationField.regex.test(value || "")) {
      return validationField.regexFailMessage || "Invalid format.";
    }

    return false;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as ValidationKeys;
    const value = e.target.value || "";

    setQuestionValue({ ...questionValue, [name]: value });

    if (error[name]) {
      setFieldError(name, validateField(name, value));
    }
  };

  const handleBlur = (e: SyntheticEvent<HTMLInputElement>) => {
    const name = (e.target as HTMLInputElement).name as ValidationKeys;

    setFieldError(name, validateField(name, questionValue[name]));

    onBlur?.(e);
  };

  const onValidateForm = (onSuccess?: (values: AnsweredType) => void) => () => {
    const newErrors: Record<string, string> = {};
    let hasError = false;

    (Object.keys(validationFields) as ValidationKeys[]).forEach((name) => {
      const value = questionValue[name];
      const errorMsg = validateField(name, value);

      if (errorMsg) {
        hasError = true;
        newErrors[name] = errorMsg;
      }
    });

    setError(newErrors);

    if (!hasError && onSuccess) {
      onSuccess(questionValue);
    }
  };

  const registerField = (name: ValidationKeys) => ({
    value: questionValue[name],
    onChange: handleInputChange,
    error: error[name],
    onBlur: handleBlur,
    name,
  });

  return { setFieldError, registerField, validateField, onValidateForm, error };
};
